/**
 * /marketplace/shopping-lists — unified UI for the Shopping Lists P0.
 *
 * Behavior depends on the `SHOPPING_LISTS_UNIFIED` env flag exposed
 * via `GET /api/shopping-lists/summary → unified_flag`:
 *   • flag OFF (default) — render "Coming soon" placeholder. The
 *     endpoints work but are gated so real users hitting this route
 *     don't see half-cooked functionality.
 *   • flag ON — render the full unified UI (add-input + pill filters
 *     + item list with inline edit / toggle / Amazon buy / delete).
 *
 * Reads from `GET /api/shopping-lists/all` with optional
 * `?trip_id=` / `?list_id=` / `?checked=false`.
 * Writes via the P0 endpoints.
 *
 * Sync with the per-trip BackpackList: broad-prefix React Query
 * invalidation on ["shopping-lists"] guarantees BackpackList (which
 * uses the same key when the flag is on) refetches immediately.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plane, Package, Check, Square, Trash2, ShoppingCart, Loader2,
  Plus, Pencil, X as XIcon, MoreHorizontal, Copy, MoveRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { goAmazon } from "@/lib/amazonClick";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";

const SHOPPING_KEY = ["shopping-lists"];

// ── data hooks ────────────────────────────────────────────────

function useSummary() {
  return useQuery({
    queryKey: ["shopping-lists", "summary"],
    queryFn: async () => (await api.get("/shopping-lists/summary")).data,
    staleTime: 30_000,
  });
}

function useShoppingItems(filters) {
  return useQuery({
    queryKey: ["shopping-lists", "all", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.trip_id) params.set("trip_id", filters.trip_id);
      if (filters.list_id) params.set("list_id", filters.list_id);
      if (filters.checked != null) params.set("checked", filters.checked ? "true" : "false");
      params.set("limit", "500");
      const url = "/shopping-lists/all" + (params.toString() ? `?${params}` : "");
      return (await api.get(url)).data;
    },
    keepPreviousData: true,
  });
}

function useBookedTrips() {
  // Small fetch of the user's booked trips so we can label pills.
  return useQuery({
    queryKey: ["shopping-lists", "booked-trips-lite"],
    queryFn: async () => {
      const { data } = await api.get("/booked-trips");
      const trips = Array.isArray(data) ? data : (data?.results || data?.trips || []);
      return trips.map((t) => ({
        id: t.id,
        name: t.name || t.destination_name || t.country || "Trip",
      }));
    },
    staleTime: 60_000,
  });
}

// ── main page ─────────────────────────────────────────────────

export default function MarketplaceShoppingListsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const qc = useQueryClient();
  const summary = useSummary();
  const flagOn = !!summary.data?.unified_flag;
  const trips = useBookedTrips();

  // Filter state — persisted in URL so refresh keeps context.
  const activeFilter = searchParams.get("f") || "all";      // 'all' | 'outstanding' | 'general' | 'trip:<uuid>'
  const setFilter = (id) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (id === "all") next.delete("f"); else next.set("f", id);
    return next;
  });

  const queryFilters = useMemo(() => {
    if (activeFilter === "outstanding") return { checked: false };
    if (activeFilter === "general") return { _general: true };
    if (activeFilter.startsWith("trip:")) return { trip_id: activeFilter.slice(5) };
    return {};
  }, [activeFilter]);

  const itemsQ = useShoppingItems(queryFilters);
  // Client-side General filter: rows with no trip_ids AND no list_ids.
  const items = useMemo(() => {
    const raw = itemsQ.data?.items || [];
    if (activeFilter === "general") {
      return raw.filter((it) => (it.trip_ids?.length || 0) === 0 && (it.list_ids?.length || 0) === 0);
    }
    return raw;
  }, [itemsQ.data, activeFilter]);

  // ── mutations ─────────────────────────────────────────────
  const invalidate = () => qc.invalidateQueries({ queryKey: SHOPPING_KEY });

  const addMut = useMutation({
    mutationFn: (payload) => api.post("/shopping-lists/items", payload).then((r) => r.data.item),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't add"),
  });
  const patchMut = useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/shopping-lists/items/${id}`, body).then((r) => r.data.item),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't save"),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/shopping-lists/items/${id}`),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't delete"),
  });

  // 2026-07-18 Fix 5 — Duplicate + Move-to-another-list.
  // Both endpoints refetch the whole ["shopping-lists"] tree so the
  // source list drops the item (on move) and the destination gains it
  // in one round-trip. Duplicate is optimistically prepended before
  // the refetch to feel snappy.
  const [rowError, setRowError] = useState(null);   // {id, msg}
  const duplicateMut = useMutation({
    mutationFn: async (item) => {
      const res = await api.post(`/shopping-lists/items/${item.id}/duplicate`);
      return res.data;
    },
    onMutate: async (item) => {
      setRowError(null);
      // Optimistic prepend against every cached "shopping-lists/all" query.
      const previous = qc.getQueriesData({ queryKey: ["shopping-lists", "all"] });
      const optimistic = {
        ...item,
        id: `optim-${item.id}-${Date.now()}`,
        checked: false,
        _optimistic: true,
      };
      qc.setQueriesData({ queryKey: ["shopping-lists", "all"] }, (old) => {
        if (!old || !Array.isArray(old.items)) return old;
        return { ...old, items: [optimistic, ...old.items], count: (old.count ?? old.items.length) + 1 };
      });
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) ctx.previous.forEach(([k, v]) => qc.setQueryData(k, v));
      // eslint-disable-next-line no-console
      console.error("[shopping] duplicate failed", err);
      setRowError({ id: _vars?.id, msg: "Couldn't duplicate this item." });
    },
    onSettled: invalidate,
  });

  const moveMut = useMutation({
    mutationFn: async ({ itemId, listId }) => {
      const res = await api.patch(`/shopping-lists/items/${itemId}/move`, { list_id: listId });
      return res.data;
    },
    onMutate: () => setRowError(null),
    onError: (err, vars) => {
      // eslint-disable-next-line no-console
      console.error("[shopping] move failed", err);
      setRowError({ id: vars?.itemId, msg: "Couldn't move this item." });
    },
    onSettled: invalidate,
  });

  // Available lists = every distinct list_id present on the user's items.
  // Sourced from the *unfiltered* dataset so that when the user is
  // viewing e.g. `?f=trip:<id>` they can still move an item onto a
  // list they can't currently see. Fires one extra query keyed by
  // an empty filter object; TanStack Query dedupes identical fetches.
  const allItemsQ = useShoppingItems({});
  const availableListIds = useMemo(() => {
    const raw = allItemsQ.data?.items || [];
    const set = new Set();
    raw.forEach((it) => (it.list_ids || []).forEach((lid) => lid && set.add(lid)));
    return Array.from(set);
  }, [allItemsQ.data]);

  // ── #30: Move-to-packing-list prompt ──────────────────────
  //
  // Fires when the user checks off (unchecked → checked) an item.
  // Two flavors:
  //   • item is attached to ≥1 trip → inline "Move to packing list
  //     for <TripA, TripB>?" prompt (mirrors the trip-Backpack flow).
  //     Yes = POST to each attached trip's /checklist with
  //     category=pack. Item stays checked on the shopping list.
  //   • item has no trip attachment → prompt "Add to a packing list?"
  //     Yes opens the trip-picker modal below.
  //
  // Uses the same `POST /booked-trips/{id}/checklist { text,
  // category:"pack" }` endpoint that the trip-Backpack "Good call"
  // prompt already uses, so items packed via the marketplace show up
  // under Organize → Packing list on the trip immediately.
  const [packPrompt, setPackPrompt] = useState(null);       // {item, trip_ids[]}
  const [packPickerOpen, setPackPickerOpen] = useState(null); // item awaiting trip pick

  const triggerToggle = (item) => {
    const willCheck = !item.checked;
    patchMut.mutate({ id: item.id, checked: willCheck });
    if (!willCheck) return;   // only prompt on check, not on uncheck
    const attached = item.trip_ids || [];
    if (attached.length > 0) {
      setPackPrompt({ item, trip_ids: attached });
    } else if ((trips.data || []).length > 0) {
      setPackPrompt({ item, trip_ids: [] });
    }
    // If user has no trips at all we skip the prompt silently — no
    // valid target and nagging them to book one is annoying.
  };

  const addToPacking = async (item, tripIds) => {
    if (!tripIds.length) return;
    const results = await Promise.allSettled(
      tripIds.map((tid) =>
        api.post(`/booked-trips/${tid}/checklist`, { text: item.name, category: "pack" }),
      ),
    );
    const okCount = results.filter((r) => r.status === "fulfilled").length;
    // 2026-07-15 #polish2 — removed success toast on the pack-to-trip
    // flow; the modal closes and the item stays checked on the list,
    // which is the visual confirmation. Errors still toast.
    if (okCount === 0) {
      toast.error("Couldn't add to packing list");
    }
  };

  // ── render ────────────────────────────────────────────────

  if (summary.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-tb-navy/70">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!flagOn) return <ComingSoon />;

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <h1 className="font-serif-it italic text-tb-navy text-3xl mb-3">Shopping Lists</h1>
        <p className="font-hand text-tb-navy/75">Log in to build your list.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="marketplace-shopping-lists">
      <div className="mb-6 flex items-baseline gap-3 flex-wrap">
        <h1 className="font-serif-it italic text-tb-navy" style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
          Shopping Lists
        </h1>
        <span className="font-hand text-tb-navy/60 text-sm">
          {summary.data?.total ?? 0} items · {summary.data?.checked ?? 0} checked
        </span>
      </div>

      <AddItemBar
        trips={trips.data || []}
        onAdd={(payload) => addMut.mutateAsync(payload)}
      />

      <FilterPills
        active={activeFilter}
        onChange={setFilter}
        trips={trips.data || []}
      />

      {itemsQ.isLoading && (
        <div className="py-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-tb-navy/60" />
        </div>
      )}

      {!itemsQ.isLoading && items.length === 0 && (
        <EmptyState filter={activeFilter} />
      )}

      {!itemsQ.isLoading && items.length > 0 && (
        <ul className="space-y-2" data-testid="shopping-items-list">
          {items.map((it) => (
            <ShoppingItemRow
              key={it.id}
              item={it}
              trips={trips.data || []}
              availableListIds={availableListIds}
              onToggle={() => triggerToggle(it)}
              onRename={(name) => patchMut.mutate({ id: it.id, name })}
              onDelete={() => delMut.mutate(it.id)}
              onDuplicate={() => duplicateMut.mutate(it)}
              onMove={(listId) => moveMut.mutate({ itemId: it.id, listId })}
              rowError={rowError && rowError.id === it.id ? rowError.msg : null}
              onSetTripIds={(add, remove) => patchMut.mutate({
                id: it.id,
                ...(add.length ? { trip_ids_add: add } : {}),
                ...(remove.length ? { trip_ids_remove: remove } : {}),
              })}
            />
          ))}
        </ul>
      )}

      {/* 2026-07-15 #30 — inline packing-list prompt (bar above the
          bottom nav) + trip-picker modal for items with no attached
          trip. Mirrors the trip-Backpack "Good call!" prompt UX so
          both surfaces feel identical. */}
      {packPrompt && packPrompt.trip_ids.length > 0 && (
        <PackToTripsPrompt
          item={packPrompt.item}
          tripIds={packPrompt.trip_ids}
          trips={trips.data || []}
          onYes={async () => {
            await addToPacking(packPrompt.item, packPrompt.trip_ids);
            setPackPrompt(null);
          }}
          onNo={() => setPackPrompt(null)}
        />
      )}
      {packPrompt && packPrompt.trip_ids.length === 0 && (
        <PackToTripsPrompt
          item={packPrompt.item}
          tripIds={[]}
          trips={trips.data || []}
          onYes={() => {
            setPackPickerOpen(packPrompt.item);
            setPackPrompt(null);
          }}
          onNo={() => setPackPrompt(null)}
        />
      )}
      {packPickerOpen && (
        <TripPickerModal
          item={packPickerOpen}
          trips={trips.data || []}
          onConfirm={async (tripIds) => {
            await addToPacking(packPickerOpen, tripIds);
            setPackPickerOpen(null);
          }}
          onClose={() => setPackPickerOpen(null)}
        />
      )}
    </div>
  );
}

// ── #30 prompt + picker ───────────────────────────────────────

function PackToTripsPrompt({ item, tripIds, trips, onYes, onNo }) {
  const tripMap = useMemo(() => {
    const m = {};
    (trips || []).forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [trips]);
  const tripNames = tripIds.map((id) => tripMap[id] || "your trip");
  const label = tripIds.length === 0
    ? "Add to a packing list?"
    : tripIds.length === 1
      ? `Move to packing list for ${tripNames[0]}?`
      : `Move to packing list for ${tripNames.slice(0, -1).join(", ")} & ${tripNames.slice(-1)}?`;
  return (
    <div
      data-testid="shopping-to-packing-prompt"
      role="dialog"
      aria-live="polite"
      className="fixed left-1/2 -translate-x-1/2 bottom-6 z-50 max-w-lg w-[92%] rounded-2xl border-2 border-tb-gold bg-tb-navy-deep text-white px-4 py-3 flex flex-wrap items-center gap-3 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-200"
    >
      <div className="min-w-0 flex-1">
        <p className="font-serif-it italic text-white text-base leading-snug">{label}</p>
        <p className="font-hand text-white/70 text-xs mt-0.5 truncate">
          <b className="not-italic text-tb-gold-bright">{item.name}</b>
        </p>
      </div>
      <div className="flex gap-2 shrink-0">
        <button
          type="button"
          data-testid="shopping-to-packing-yes"
          onClick={onYes}
          className="px-3 py-1.5 rounded-full bg-tb-gold text-tb-navy font-semibold text-sm tb-tap"
        >
          Yes
        </button>
        <button
          type="button"
          data-testid="shopping-to-packing-no"
          onClick={onNo}
          className="px-3 py-1.5 rounded-full border-2 border-white/40 text-white font-semibold text-sm tb-tap hover:border-white/70"
        >
          Not now
        </button>
      </div>
    </div>
  );
}

function TripPickerModal({ item, trips, onConfirm, onClose }) {
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  if (!trips.length) {
    return (
      <div
        data-testid="shopping-trip-picker-modal"
        className="fixed inset-0 z-50 flex items-center justify-center bg-tb-navy/60 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <div className="bg-white rounded-2xl border-2 border-tb-gold shadow-2xl max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
          <p className="font-serif-it italic text-tb-navy text-xl">No booked trips yet</p>
          <p className="font-hand text-tb-navy/70 mt-2">Pack this once you book something.</p>
          <button onClick={onClose} className="mt-5 px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright font-hand tb-tap">
            Got it
          </button>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="shopping-trip-picker-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tb-navy/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl border-2 border-tb-gold shadow-2xl max-w-md w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b-2 border-tb-cream-3">
          <p className="font-serif-it italic text-tb-navy text-xl leading-tight">
            Which trip's packing list?
          </p>
          <p className="font-hand text-tb-navy/60 text-xs mt-1 truncate">
            <b className="not-italic">{item.name}</b>
          </p>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2">
          {trips.map((t) => {
            const active = sel.includes(t.id);
            return (
              <button
                key={t.id}
                type="button"
                data-testid={`shopping-trip-picker-option-${t.id}`}
                onClick={() => toggle(t.id)}
                className={
                  "w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 my-1 text-left tb-tap " +
                  (active
                    ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                    : "bg-white text-tb-navy border-tb-cream-3 hover:border-tb-navy/40")
                }
              >
                <span className={"w-5 h-5 rounded flex items-center justify-center border-2 " + (active ? "bg-tb-gold border-tb-gold text-tb-navy" : "border-tb-navy/30")}>
                  {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                </span>
                <span className="font-hand text-sm truncate">{t.name}</span>
              </button>
            );
          })}
        </div>
        <div className="px-4 py-3 border-t-2 border-tb-cream-3 flex gap-2 justify-end">
          <button
            type="button"
            data-testid="shopping-trip-picker-cancel"
            onClick={onClose}
            className="px-4 py-2 rounded-full border-2 border-tb-cream-3 text-tb-navy font-hand text-sm tb-tap"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="shopping-trip-picker-confirm"
            disabled={sel.length === 0}
            onClick={() => onConfirm(sel)}
            className="px-4 py-2 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm tb-tap disabled:opacity-50"
          >
            Add to {sel.length || 0} trip{sel.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── subcomponents ─────────────────────────────────────────────

function ComingSoon() {
  return (
    <div className="min-h-[60vh] max-w-lg mx-auto flex flex-col items-center justify-center text-center px-6 py-14">
      <div className="w-16 h-16 rounded-full bg-tb-navy/5 border-2 border-tb-gold/60 flex items-center justify-center mb-4">
        <ShoppingCart className="w-7 h-7 text-tb-gold-soft" strokeWidth={1.75} />
      </div>
      <h1 className="font-serif-it italic text-tb-navy text-3xl md:text-4xl leading-tight">
        Shopping Lists — Coming Soon
      </h1>
      <p className="mt-3 font-hand text-tb-navy/75 max-w-md">
        We're building a unified shopping list that spans every one of your trips.
        In the meantime, use the Backpack tab on any booked trip.
      </p>
    </div>
  );
}

function EmptyState({ filter }) {
  const msg =
    filter === "general" ? "No everyday travel essentials yet. Add something above."
      : filter.startsWith("trip:") ? "No items for this trip. Add something above."
      : "No shopping items yet. Add something above.";
  return (
    <div className="py-16 text-center font-hand text-tb-navy/60" data-testid="shopping-empty">
      {msg}
    </div>
  );
}

function FilterPills({ active, onChange, trips }) {
  const pills = [
    { id: "all",     label: "All",                         testId: "shopping-filter-all" },
    { id: "general", label: "Everyday travel essentials",  testId: "shopping-filter-general" },
    ...trips.map((t) => ({
      id: `trip:${t.id}`,
      label: t.name,
      testId: `shopping-filter-trip-${t.id}`,
      icon: Plane,
    })),
  ];
  return (
    <div
      className="flex items-center gap-2 overflow-x-auto py-2 mb-4 -mx-4 px-4"
      role="tablist"
      data-testid="shopping-filter-pills"
    >
      {pills.map(({ id, label, testId, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={testId}
            onClick={() => onChange(id)}
            className={
              "shrink-0 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border-2 tb-tap transition font-hand text-sm " +
              (isActive
                ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                : "bg-white text-tb-navy border-tb-navy/25 hover:border-tb-navy/50")
            }
          >
            {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.9} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

function AddItemBar({ trips, onAdd }) {
  const [name, setName] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [tripIds, setTripIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName(""); setExpanded(false); setTripIds([]);
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      await onAdd({
        name: trimmed,
        ...(tripIds.length ? { trip_ids: tripIds } : {}),
      });
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTrip = (id) => setTripIds((prev) =>
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="rounded-2xl border-2 border-tb-cream-3 bg-tb-cream p-3 md:p-4 mb-4">
      <div className="flex items-stretch gap-2">
        <input
          data-testid="shopping-add-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Add an item…"
          className="flex-1 h-11 px-4 rounded-full bg-white border-2 border-tb-navy/20 focus:border-tb-gold outline-none font-ui text-sm text-tb-navy"
        />
        {trips.length > 0 && (
          <button
            type="button"
            data-testid="shopping-add-toggle-detail"
            onClick={() => setExpanded((x) => !x)}
            aria-label={expanded ? "Hide trip options" : "Attach to trip"}
            className="h-11 px-3 rounded-full border-2 border-tb-navy/20 bg-white text-tb-navy tb-tap hover:border-tb-navy/40"
          >
            {expanded ? <XIcon className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
          </button>
        )}
        <button
          type="button"
          data-testid="shopping-add-submit"
          onClick={submit}
          disabled={!name.trim() || submitting}
          className="h-11 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand tb-tap disabled:opacity-50"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}
        </button>
      </div>
      {expanded && trips.length > 0 && (
        <div className="mt-3">
          <div className="font-hand text-xs text-tb-navy/70 mb-1">
            Attach to trip(s) — leave empty for everyday travel essentials
          </div>
          <div className="flex flex-wrap gap-1.5">
            {trips.map((t) => {
              const active = tripIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTrip(t.id)}
                  className={
                    "px-3 h-8 rounded-full border-2 font-hand text-xs tb-tap " +
                    (active
                      ? "bg-tb-navy text-tb-gold-bright border-tb-gold"
                      : "bg-white text-tb-navy border-tb-navy/25 hover:border-tb-navy/50")
                  }
                >
                  {t.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ShoppingItemRow({ item, trips, availableListIds = [], onToggle, onRename, onDelete, onSetTripIds, onDuplicate, onMove, rowError }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);
  const [amazonLoading, setAmazonLoading] = useState(false);

  const tripMap = useMemo(() => {
    const m = {};
    (trips || []).forEach((t) => { m[t.id] = t.name; });
    return m;
  }, [trips]);

  // Move-to-list submenu targets = distinct list_ids on the user's
  // account minus the ones already on this item.
  const moveTargets = useMemo(() => {
    const current = item.list_ids || [];
    return (availableListIds || []).filter((lid) => !current.includes(lid));
  }, [availableListIds, item.list_ids]);
  const isOptimistic = !!item._optimistic;

  const saveEdit = () => {
    const v = (draft || "").trim();
    if (!v || v === item.name) { setEditing(false); setDraft(item.name); return; }
    onRename(v);
    setEditing(false);
  };

  return (
    <li
      data-testid={`shopping-item-${item.id}`}
      className={
        "rounded-xl border-2 border-tb-cream-3 bg-white px-3 py-2.5 " +
        (item.checked ? "opacity-60 " : "") +
        (isOptimistic ? "animate-pulse " : "")
      }
    >
      <div className="flex items-center gap-3">
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={saveEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveEdit();
              if (e.key === "Escape") { setDraft(item.name); setEditing(false); }
            }}
            data-testid={`shopping-name-input-${item.id}`}
            className="w-full bg-transparent border-b-2 border-tb-navy/30 focus:border-tb-gold outline-none font-ui text-tb-navy text-sm"
          />
        ) : (
          <button
            type="button"
            data-testid={`shopping-name-${item.id}`}
            onClick={() => setEditing(true)}
            className={
              "block w-full text-left font-ui text-tb-navy text-sm truncate " +
              (item.checked ? "line-through text-tb-navy/60" : "")
            }
          >
            {item.name}
          </button>
        )}
        {(item.trip_ids?.length || item.category) ? (
          <div className="mt-1 flex flex-wrap gap-1">
            {item.category && (
              <span className="inline-flex items-center gap-1 rounded-full bg-tb-navy/5 border border-tb-navy/15 px-2 py-0.5 font-hand text-[10px] text-tb-navy/70">
                <Package className="w-3 h-3" />
                {item.category}
              </span>
            )}
            {(item.trip_ids || []).map((tid) => (
              <span
                key={tid}
                data-testid={`shopping-trip-badge-${item.id}-${tid}`}
                className="inline-flex items-center gap-1 rounded-full bg-tb-gold/15 border border-tb-gold/40 px-2 py-0.5 font-hand text-[10px] text-tb-navy"
              >
                <Plane className="w-3 h-3" />
                {tripMap[tid] || tid.slice(0, 6) + "…"}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div className="shrink-0 flex items-center gap-1">
        {/* 2026-07-15 #26: Amazon URL removed from schema — the pill
            asks the backend to build a tagged search URL for
            `item.name` on click. Always visible now (was gated behind
            `item.amazon_url` before). */}
        <button
          type="button"
          data-testid={`shopping-amazon-${item.id}`}
          onClick={async (e) => {
            e.stopPropagation();
            await goAmazon({
              itemName: item.name,
              itemId: item.id,
              onLoadingChange: setAmazonLoading,
            });
          }}
          title={`Search Amazon for "${item.name}"`}
          aria-label="Buy on Amazon"
          className="h-8 px-3 rounded-full border-2 border-tb-gold/60 bg-tb-gold/20 text-tb-navy hover:brightness-105 font-hand text-xs inline-flex items-center gap-1 tb-tap"
        >
          {amazonLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShoppingCart className="w-3.5 h-3.5" />}
          Buy on Amazon
        </button>
        <button
          type="button"
          data-testid={`shopping-toggle-${item.id}`}
          onClick={onToggle}
          aria-label={item.checked ? "Uncheck" : "Check off"}
          aria-pressed={item.checked}
          className={
            "shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 tb-tap " +
            (item.checked
              ? "bg-tb-forest text-white border-tb-forest"
              : "bg-white text-tb-forest border-tb-forest hover:bg-tb-forest-soft")
          }
        >
          {item.checked ? <Check className="w-4 h-4" /> : <Square className="w-4 h-4" strokeWidth={1.8} />}
        </button>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Rename"
            data-testid={`shopping-edit-${item.id}`}
            className="w-8 h-8 rounded-full text-tb-navy/60 hover:text-tb-navy hover:bg-tb-navy/5 flex items-center justify-center tb-tap"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          aria-label="Delete"
          data-testid={`shopping-delete-${item.id}`}
          className="w-8 h-8 rounded-full text-tb-navy/60 hover:text-tb-coral hover:bg-tb-coral/10 flex items-center justify-center tb-tap"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
        {!isOptimistic && (onDuplicate || onMove) && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="More actions"
                data-testid={`shopping-item-menu-${item.id}`}
                className="w-8 h-8 rounded-full text-tb-navy/60 hover:text-tb-navy hover:bg-tb-navy/5 flex items-center justify-center tb-tap"
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {onDuplicate && (
                <DropdownMenuItem
                  data-testid={`shopping-item-duplicate-${item.id}`}
                  onSelect={(e) => { e.preventDefault(); onDuplicate(); }}
                  className="font-hand text-tb-navy cursor-pointer"
                >
                  <Copy className="w-3.5 h-3.5 mr-2" />
                  Duplicate
                </DropdownMenuItem>
              )}
              {onMove && (
                moveTargets.length === 0 ? (
                  <DropdownMenuItem
                    disabled
                    data-testid={`shopping-item-move-${item.id}-none`}
                    className="font-hand text-tb-navy/40 cursor-not-allowed"
                  >
                    <MoveRight className="w-3.5 h-3.5 mr-2" />
                    No other lists
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      data-testid={`shopping-item-move-trigger-${item.id}`}
                      className="font-hand text-tb-navy cursor-pointer"
                    >
                      <MoveRight className="w-3.5 h-3.5 mr-2" />
                      Move to another list…
                    </DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                      <DropdownMenuSubContent className="min-w-[200px] max-h-[280px] overflow-y-auto">
                        {moveTargets.map((lid) => (
                          <DropdownMenuItem
                            key={lid}
                            data-testid={`shopping-item-move-${item.id}-${lid}`}
                            onSelect={(e) => { e.preventDefault(); onMove(lid); }}
                            className="font-hand text-tb-navy cursor-pointer"
                          >
                            <Package className="w-3.5 h-3.5 mr-2 text-tb-navy/60" />
                            List {lid.slice(0, 8)}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuPortal>
                  </DropdownMenuSub>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      </div>
      {rowError && (
        <div
          data-testid={`shopping-item-error-${item.id}`}
          role="alert"
          className="mt-2 rounded-lg border-2 border-tb-coral/50 bg-tb-coral/10 px-3 py-1.5 font-hand text-xs text-tb-coral"
        >
          {rowError}
        </div>
      )}
    </li>
  );
}
